import React, { useState } from 'react';
import Card from '../../components/common/Card';
import { Divider } from '../../components/sidebar/components/Divider';
import calendar from '../../assets/date.svg';
import planIcon from '../../assets/plan.svg';
import ScrollArea from '../../components/common/scrollArea';
import plancheck from '../../assets/plancheck.svg';
import { usePayment } from '@/hooks/usePayment';
import { useOrganization } from '@/hooks/useOrganization';
import { plans } from '@/data/plans';
import { useSubscriptionDays } from '@/hooks/useSubscriptionDays';

const PlanPage = () => {
  const { organization } = useOrganization();
  const { createCheckout, isCreatingCheckout, processingPlanId } = usePayment();
  const { daysRemaining } = useSubscriptionDays();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handlePurchase = async (planId: string) => {
    try {
      setIsRedirecting(true);
      await createCheckout(planId);
    } catch (error) {
      console.error('Error creating checkout:', error);
      setIsRedirecting(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="flex flex-col w-[762px] text-[Nunito Sans]">
        
        {/* Page path */}
        <div className="flex items-start mt-5 mb-2">
          <div className="text-start text-sm font-light text-[#75777C]">
            Workspace settings
          </div>
          <div className="text-start text-sm font-light text-[#222222] pl-2">
            / Plan
          </div>
        </div>

        <ScrollArea
          maxHeight="calc(100vh - 80px)"
        >
          <div className="flex flex-col">
            {/* Page title */}

            <div className="flex flex-col mb-7">
              <div className="flex flex-col mt-5 text-left gap-[2px]">
                <div className="text-xl font-semibold text-[#222222] ">Plan</div>
                <div className="text-sm font-light text-[#75777C] ">
                  View your plan information or switch plans according to your needs
                </div>
              </div>
            </div>

            <Divider className="w-full bg-[#EEEFF1] " />

            {/* Trial Notice */}
            {organization?.current_plan === 'free' && daysRemaining > 0 && (
              <div className="flex justify-between items-center bg-gray-50 px-[14px] py-[8px] rounded-[8px] my-7">
                <div className="flex items-center gap-2">
                  <img src={calendar} alt="Icon" className="w-[14px] h-[14px]" />
                  <div className="text-sm font-light text-[#5C5E63]">
                    There are {daysRemaining} days left on your subscription
                  </div>
                </div>
              </div>
            )}

            {/* Plans Section */}
            <div className="flex flex-col pt-7">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Compare plans</h2>
                <div className="flex items-center gap-2">
                  <div className="text-sm font-light text-[#000000]">Billing period</div>
                  <div className="flex items-center gap-2 border border-[#DDDDDD] rounded-[8px] px-8 py-1">
                    <div className="text-sm font-light text-[#222222]">Monthly</div>
                  </div>
                </div>
              </div>


              {/* Plan Details */}
              <div className="grid grid-cols-3 gap-4 mt-12">
                {plans.map((plan) => (
                  <div key={plan.name} className="relative">
                    {plan.isPopular && (
                      <div className="">
                        <div className="absolute -top-[44px] w-[240px] text-center bg-[#29DC5B] text-sm font-normal text-[#222222] pb-12 rounded-[12px] border border-[#EEEEEE]">
                          <div className="text-[12px] font-light text-[#000000]  text-center pt-[12px] pb-2">
                            Most popular
                          </div>
                        </div>
                      </div>
                    )}
                    <Card className="relative p-4 w-[240px] rounded-[12px]">
                      <div className="flex flex-col items-s tart w-full">
                        <div className="flex flex-col items-start gap-3 mb-6 w-full">
                          <div className="w-[32px] h-[32px] bg-[#E8FFF0] rounded-lg flex items-center justify-center">
                            <img src={planIcon} alt="Icon" className="w-[30px] h-[30px]" />
                          </div>
                          <h3 className="text-[20px] font-semibold text-[#222222] text-left">{plan.name}</h3>
                        </div>

                        <div className="mb-6 text-left w-full">
                          <div className="text-[32px] font-bold text-[#222222]">${plan.price}</div>
                          <div className="text-sm text-[#75777C]">Per user/month, billed monthly</div>
                        </div>

                        <button
                          onClick={() => handlePurchase(plan.id)}
                          disabled={isCreatingCheckout && processingPlanId === plan.id || plan.id === organization?.current_plan || isRedirecting}
                          className={`w-full h-[28px] rounded-lg mb-6 text-[14px] font-light leading-[20px] tracking-[-0.28px] text-center ${
                            plan.id === organization?.current_plan
                              ? 'bg-[#81F4A1] text-[#222222] px-4'
                              : 'bg-[#FFFFFF] border border-[#DDDDDD] text-[#222222] px-4 hover:border-[#29DC5B] transition-colors'
                          }`}
                        >
                          {plan.id === organization?.current_plan 
                            ? 'Current plan' 
                            : isCreatingCheckout && processingPlanId === plan.id || isRedirecting
                              ? 'Processing...' 
                              : 'Purchase'}
                        </button>

                        <div className="w-full">
                          <div className="text-sm font-normal text-[#222222] mb-4 text-left">What's included</div>
                          <ul className="space-y-3 w-full">
                            {plan.features.map((feature: string, index: number) => (
                              <li key={index} className="flex items-start gap-[4px] text-[12px] font-light text-[#7A7A7A]">
                                <img src={plancheck} alt="Icon" className="w-[14px] h-[14px] bg-[#F5F5F5] rounded-[6px]" />
                                <span className="leading-tight text-left">{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}

                {/* Enterprise Plan */}
                <div className="col-span-3">
                  <Card className="p-4 rounded-[12px] mb-[120px]">
                    <div className="grid grid-cols-3 gap-4 items-center">
                      {/* Title and Price Column */}
                      <div className="flex items-start gap-3 w-full">
                        <div className="w-[32px] h-[32px] bg-[#E8FFF0] rounded-lg flex items-center justify-center">
                          <img src={planIcon} alt="Icon" className="w-[30px] h-[30px]" />
                        </div>
                        <div>
                          <h3 className="text-[20px] font-semibold text-[#222222] text-left">Enterprise</h3>
                        </div>
                      </div>

                      {/* Features Column */}
                      <div className="flex flex-col w-full">
                        <ul className="space-y-3">
                          <li className="flex items-start gap-[4px] text-[12px] font-light text-[#7A7A7A]">
                            <img src={plancheck} alt="Icon" className="w-[14px] h-[14px] bg-[#F5F5F5] rounded-[6px]" />
                            <span className="leading-tight text-left">Unlimited verified leads</span>
                          </li>
                          <li className="flex items-start gap-[4px] text-[12px] font-light text-[#7A7A7A]">
                            <img src={plancheck} alt="Icon" className="w-[14px] h-[14px] bg-[#F5F5F5] rounded-[6px]" />
                            <span className="leading-tight text-left">Custom integrations</span>
                          </li>
                          <li className="flex items-start gap-[4px] text-[12px] font-light text-[#7A7A7A]">
                            <img src={plancheck} alt="Icon" className="w-[14px] h-[14px] bg-[#F5F5F5] rounded-[6px]" />
                            <span className="leading-tight text-left">Dedicated account manager</span>
                          </li>
                        </ul>
                      </div>

                      {/* Button Column */}
                      <div className="flex justify-center w-full">
                        <button
                          className="h-[28px] rounded-lg text-[14px] font-medium leading-[20px] tracking-[-0.28px] text-center bg-[#FFFFFF] border border-[#DDDDDD] text-[#222222] px-8 hover:border-[#29DC5B] transition-colors"
                          onClick={() => window.open('https://calendly.com/rachit-clodexa/clodexa-enterprise', '_blank')}
                        >
                          Contact sales team
                        </button>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default PlanPage; 